import { useAuth } from 'Providers/auth/AuthProvider.tsx';
import {
  DatapointFieldsFragment_,
  ShortUser,
  useUpsertDatapointMutation,
  Esrs_Answer_Constraint_,
  Esrs_Answer_Update_Column_,
  GetEsrsMetricAnswerDocument_,
  Esrs_DatapointTag_Constraint_,
  AttachmentBox_Constraint_,
  AttachmentBox_Update_Column_,
  useGetEsrsMetricsAnswersSubscription,
  GetEsrsMetricsDatapointsDocument_,
  NoteHistory_Constraint_,
  NoteHistory_Update_Column_,
  useGetSingleEsrsMetricAnswerQuery,
  GetMetricAnswersDocument_,
  EsrsAssessmentAnswersAggregateDocument_,
  Esrs_DatapointChoice_Constraint_,
  useDeleteDatapointChoicesMutation,
  GetAnswersForMetricsOnCompanyLevelDocument_,
  GetAnswersForDrMetricsOnGroupLevelDocument_,
  GetSubAnswersPerDrOnGroupLevelDocument_,
  GetSubMaterialMetricsPerDrOnGroupLevelDocument_,
  GetAnswersForDrMetricsOnCompanyLevelDocument_,
  TaskStatusEnum_Enum_,
  useUpdateTaskInProgressMutation,
} from 'models';
import { useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { TableData } from 'Molecules/NestedTable';
import { FrequencyEnums, TimePeriods, TIME_FRAMES_ARRAY } from '../Requirement';
import {
  ESRS_KNOWN_VARIABLES,
  filterByCorrectTags,
  filterByHasTags,
  resolveMetricCalculation,
  showResultAsPercentage,
} from './AggregatedMetrics/calculations';
import { AggregatedQualitativeAnswers, getNestedMetrics } from './AggregatedMetrics';
import { uniq } from 'lodash';
import { GroupMaterialMetrics } from './AggregatedMetrics/AggregatedMetricsUtils';
import { MaterialMetricTag } from '../../DataCollection';
import { AssessableMetrics } from './Metrics';
import { getTagsCombination, NOTAGCOMBINATION } from 'containers/Esrs/utils';

export type TagOptions = {
  tagType: string;
  valueOptions: Array<{
    __typename?: 'esrs_MaterialTagValue';
    tagValue: string;
  }>;
  [x: string]: any;
}[];
type Combination = Record<string, string>;
type MaterialTagValue = MaterialMetricTag['materialTagValues'][number];

type MetricsExtraData = {
  metric: AssessableMetrics[number];
  tagCombination?: string;
  lastEditedByName?: string;
  lastEditedDate?: string;
  tags?: {
    tagType: string;
    tagValue: string;
  }[];
  referenceToSource?: string;
  tagName?: string;
  tagType?: string;
  isChild?: boolean;
  parentMetric?: AssessableMetrics[number];
  hidden?: boolean;
  sourceData?: AggregatedQualitativeAnswers[number];
  sourceDR?: string;
};

export type MetricsTableData = TableData<MetricsExtraData>;

type PartialMetricsData = Omit<MetricsTableData['metric'], 'childrenMetrics'>;
export interface MaybeHasChild extends PartialMetricsData {
  childrenMetrics?: { childMetric?: MaybeHasChild | null }[];
}

export const useEsrsAnswer = (metricRef: string, companyReportingUnit?: string) => {
  const [upsertDatapoint, { loading: isSaving }] = useUpsertDatapointMutation();
  const [updateTaskInProgress] = useUpdateTaskInProgressMutation();
  const [deleteChoices] = useDeleteDatapointChoicesMutation();
  const { user } = useAuth();
  const { reportingUnitId, esrsAssessmentId = '' } = useParams();
  const { data, loading, refetch } = useGetSingleEsrsMetricAnswerQuery({
    variables: {
      reportingUnitId: reportingUnitId || companyReportingUnit,
      assessmentId: esrsAssessmentId,
      metricRef,
    },
    skip: !esrsAssessmentId || !metricRef || (!reportingUnitId && !companyReportingUnit),
  });
  const answer = useMemo(() => {
    return data?.esrs_Answer[0];
  }, [data]);

  const datapoints: DatapointFieldsFragment_[] = useMemo(() => {
    return data?.esrs_Answer[0]?.datapoints ?? [];
  }, [data]);

  const onDatapointChange = useCallback(
    async ({
      value,
      factValue,
      hasOptedOut,
      optOutReason,
      dp,
      tags,
      choices,
      metadata,
    }: {
      value: string | null;
      factValue?: string | null;
      hasOptedOut: boolean;
      optOutReason: string;
      dp: Partial<DatapointFieldsFragment_>;
      tags?: {
        tagType: string;
        tagValue: string;
      }[];
      choices?: string[];
      metadata?: { [key: string]: any };
    }) => {
      const choicesToDelete = dp?.datapointChoices
        ?.filter((d) => !choices?.includes(d.choiceRef))
        ?.map((c) => c.choiceRef);

      if (!!choicesToDelete?.length) {
        await deleteChoices({
          variables: {
            datapointId: dp.id,
            choices: choicesToDelete,
          },
        });
      }
      const isTaskNotStarted =
        dp?.task?.id && dp?.task?.status === TaskStatusEnum_Enum_.NotStarted_;

      await upsertDatapoint({
        variables: {
          objects: {
            value: value,
            factValue: factValue,
            timeframe: dp?.timeframe,
            tagCombination: getTagsCombination(tags ?? []),
            authorId: user?.id,
            source: null,
            metadata: metadata,
            lastEdited: new Date(),
            datapointTags: {
              data: tags ?? [],
              on_conflict: {
                constraint:
                  Esrs_DatapointTag_Constraint_.DatapointTagDatapointIdTagValueTagTypeKey_,
              },
            },
            datapointChoices: {
              data: choices?.map((choice) => ({ choiceRef: choice })) ?? [],
              on_conflict: {
                constraint:
                  Esrs_DatapointChoice_Constraint_.DatapointChoiceDatapointIdChoiceRefKey_,
              },
            },
            answer: {
              data: {
                metricRef: metricRef,
                hasOptedOut: hasOptedOut,
                optOutReason: optOutReason,
                reportingUnitId: reportingUnitId || companyReportingUnit,
                assessmentId: esrsAssessmentId,
                attachmentBox: {
                  data: {},
                  on_conflict: {
                    constraint: AttachmentBox_Constraint_.AttachmentBoxMetricAnswerIdKey1_,
                    update_columns: [AttachmentBox_Update_Column_.MetricAnswerId_],
                  },
                },
                noteHistory: {
                  data: {},
                  on_conflict: {
                    constraint: NoteHistory_Constraint_.NoteHistoryMetricAnswerIdKey_,
                    update_columns: [NoteHistory_Update_Column_.MetricAnswerId_],
                  },
                },
                optOutAuthorId: user?.id,
              },
              on_conflict: {
                constraint: Esrs_Answer_Constraint_.AnswerMetricRefAssessmentIdReportingUnitIdKey_,
                update_columns: [
                  Esrs_Answer_Update_Column_.HasOptedOut_,
                  Esrs_Answer_Update_Column_.OptOutReason_,
                  Esrs_Answer_Update_Column_.OptOutAuthorId_,
                ],
              },
            },
          },
        },
        refetchQueries: [
          GetEsrsMetricAnswerDocument_,
          GetEsrsMetricsDatapointsDocument_,
          GetMetricAnswersDocument_,
          EsrsAssessmentAnswersAggregateDocument_,
          GetAnswersForMetricsOnCompanyLevelDocument_,
          GetAnswersForDrMetricsOnCompanyLevelDocument_,
          GetAnswersForDrMetricsOnGroupLevelDocument_,
          GetSubAnswersPerDrOnGroupLevelDocument_,
          GetSubMaterialMetricsPerDrOnGroupLevelDocument_,
        ],
      });

      if (isTaskNotStarted) {
        await updateTaskInProgress({
          variables: {
            id: dp.task?.id,
          },
        });
      }
      refetch();
    },
    [upsertDatapoint, datapoints]
  );
  return {
    answer,
    datapoints,
    isSaving,
    loading,
    onDatapointChange,
  };
};

export const isFrequencyYearly = (metric: MaybeHasChild): boolean => {
  const materialMetric = metric.materialMetrics?.[0];
  if (!!metric?.childrenMetrics?.length) {
    return metric.childrenMetrics?.some(
      (child) => child.childMetric && isFrequencyYearly(child.childMetric)
    );
  }
  return materialMetric?.frequency === FrequencyEnums.yearly;
};

export const areArraysOfObjectsEqual = (arr1: Combination[] = [], arr2: Combination[] = []) => {
  if (arr1.length !== arr2.length) {
    return false;
  }

  const sortedArr1 = [...arr1].sort((a, b) => {
    return a?.tagType.localeCompare(b?.tagType);
  });
  const sortedArr2 = [...arr2].sort((a, b) => {
    return a?.tagType.localeCompare(b?.tagType);
  });
  return sortedArr1.every((item, index) =>
    Object.keys(item).every((key) => item[key] === sortedArr2[index][key])
  );
};

export const addUpValues = (values: Array<string | number>) => {
  if (values.some((x) => typeof x === 'string')) return ESRS_KNOWN_VARIABLES.NONE;
  return values.reduce((a, b) => Number(a) + Number(b), 0);
};

const getRefsFromSubRows = (row: MetricsTableData): string[] => {
  const subRowRefs = row?.subRows?.flatMap((subrow) => getRefsFromSubRows(subrow)) ?? []; // Recursively get references from subRows

  const metricRefs = [row?.metric?.reference, row?.parentMetric?.reference].filter(
    (ref): ref is string => !!ref
  );

  return [...metricRefs, ...subRowRefs];
};

type ExtendedDatapoint = {
  value?: any;
  timeframe: string;
  updatedAt: string;
  answerId: string;
  metricRef: string;
  tagValues: string[];
  subsidiaryId?: string;
};

export const convertCurrency = (dpValue: number, conversionRate?: number) => {
  return conversionRate ? Number(dpValue ?? 0) * conversionRate : Number(dpValue ?? 0);
};

export const getUniqueDataPoints = (data: ExtendedDatapoint[]) => {
  const uniqueMap = data.reduce<{ [key: string]: any }>((acc, datapoint) => {
    // Create a unique key based on answerId, stringified tagValues, and timeframe
    const key = `${datapoint.answerId}-${JSON.stringify(datapoint.tagValues)}-${
      datapoint.timeframe
    }`;

    // If the key doesn't exist in the acc or the current datapoint is newer: add/replace it
    if (!acc[key] || new Date(datapoint.updatedAt) > new Date(acc[key].updatedAt)) {
      acc[key] = datapoint;
    }

    return acc;
  }, {});

  return Object.values(uniqueMap);
};

export const useAggregatedRowValue = (
  row: MetricsTableData,
  isYearly: boolean,
  companyReportingUnit?: string,
  ignoreTags?: boolean,
  nestedMetrics?: MetricsTableData[]
) => {
  const metricRow = useMemo(() => {
    if (row?.subRows?.some((subR) => subR.referenceToSource)) {
      return nestedMetrics?.find((m) => m.metric.reference === row.metric.reference) ?? row;
    }
    return row;
  }, [row, nestedMetrics]);
  const { reportingUnitId, esrsAssessmentId = '' } = useParams();
  const metricRefs = useMemo(() => uniq(getRefsFromSubRows(metricRow)), [metricRow]);

  const { data, loading } = useGetEsrsMetricsAnswersSubscription({
    variables: {
      reportingUnitId: reportingUnitId || companyReportingUnit,
      assessmentId: esrsAssessmentId,
      metricRefs,
    },
    skip:
      !esrsAssessmentId ||
      !metricRefs ||
      metricRefs.length === 0 ||
      (!reportingUnitId && !companyReportingUnit),
  });
  const metricWithChildren = useMemo(() => getNestedMetrics(metricRow), [metricRow]);
  //here
  const isMetricWithChildrenAndTags = useMemo(
    () =>
      !!metricRow?.metric?.childrenMetrics?.length &&
      metricRow?.metric.materialMetrics?.[0]?.materialMetricTags?.length,
    [metricRow]
  );

  const datapoints = useMemo(() => {
    // This deals with a metric that has children (tags) but DID NOT USE TO, so it has some old dps that are not tagged

    const dps = data?.answers.flatMap((answer) => {
      const currentMetric = metricWithChildren.find((m) => m.metric.reference === answer.metricRef);
      const currentMaterialMetric = currentMetric?.metric?.materialMetrics?.find(
        (mm) => mm.materialStandard.esrsAssessment.id === esrsAssessmentId
      );
      const parentMaterialMetric = metricWithChildren.find((m) =>
        m.metric.childrenMetrics?.some((c) => c.childMetric?.reference === answer.metricRef)
      )?.metric?.materialMetrics?.[0];
      const hasTags =
        currentMaterialMetric?.materialMetricTags?.some(
          (mTag) => (mTag.materialTagValues.length ?? 0) > 0
        ) ||
        parentMaterialMetric?.materialMetricTags?.some(
          (mTag) => (mTag.materialTagValues.length ?? 0) > 0
        );

      const isATagOfAParentWithChildren =
        !metricRow.tagType && !!metricRow.tagName && metricRow.metric.childrenMetrics?.length;

      return answer.datapoints
        .map((dp) => ({
          ...dp,
          metricRef: answer.metricRef,
          tagValues: dp.datapointTags?.map((t) => t.tagValue) ?? [],
        }))
        .filter((dp) => {
          if (ignoreTags || isMetricWithChildrenAndTags) return true;
          if (isATagOfAParentWithChildren) {
            return dp.tagValues.includes(metricRow?.tagName ?? '');
          }
          if (hasTags) {
            const rowWithNestedTags = currentMetric?.subRows
              ?.flatMap((subRow) => subRow.subRows)
              .filter((r) => !!r) as TableData<MetricsExtraData>[] | undefined;
            const tagRows = !!rowWithNestedTags?.length
              ? rowWithNestedTags
              : currentMetric?.tags?.length
                ? [currentMetric]
                : currentMetric?.subRows;

            return tagRows?.some((subRow) =>
              areArraysOfObjectsEqual(subRow.tags ?? [], dp.datapointTags)
            );
          }
          if (!hasTags && !answer.metric.childrenMetrics?.length) {
            return dp.tagValues.length <= 0 &&
              currentMaterialMetric?.frequency === FrequencyEnums.yearly
              ? dp.timeframe === 'Year'
              : dp.timeframe !== 'Year';
          }
          if (!!answer.metric.childrenMetrics?.length) {
            return false;
          }
          return true;
        });
    });

    return getUniqueDataPoints(dps ?? []);
  }, [data, ignoreTags, metricWithChildren]);

  const datapointsPerTimeframe = useMemo(() => {
    return TIME_FRAMES_ARRAY.map((timeframe) => {
      const correspondingDatapoint = datapoints?.find((datapoint) => {
        if (datapoint.timeframe !== timeframe) return false;

        if (ignoreTags) return true;
        if (metricRow.tags?.length && !datapoint.datapointTags.length) {
          return false;
        }
        // Row IS a child with a tag
        if (
          (!!metricRow.tagName && !!row.subRows?.length) ||
          !!metricRow.tagType ||
          isMetricWithChildrenAndTags
        ) {
          const subRows = [
            ...(metricRow?.subRows ?? []),
            ...(metricRow?.subRows?.flatMap((s) => s?.subRows) ?? []),
          ].filter((t) => t !== undefined);
          return subRows?.some((subRow) => filterByCorrectTags(datapoint, [subRow?.tags ?? []]));
        }
        if (metricRow?.subRows && filterByHasTags(datapoint, false)) {
          return true;
        }
        // Get ones with tags
        if (metricRow?.tags?.length) {
          return filterByCorrectTags(datapoint, [metricRow?.tags]);
        }

        // Get ones without tags
        return filterByHasTags(datapoint, false);
      });
      return { field: timeframe, value: correspondingDatapoint };
    });
  }, [datapoints, metricRow, ignoreTags]);

  const totalPerTimeframe = useMemo(() => {
    const allTimeFrames = [
      ...new Set(datapointsPerTimeframe?.map((datapoint) => datapoint.field)),
    ] as TimePeriods[];

    return allTimeFrames.reduce(
      (totals, timeframe) => {
        const correctDps = datapoints
          ?.filter((dp) => dp.timeframe === timeframe)
          .filter((dp) => {
            if (ignoreTags) return true;
            if (isMetricWithChildrenAndTags) {
              const subRows = [
                ...(metricRow?.subRows ?? []),
                ...(metricRow?.subRows?.flatMap((s) => s?.subRows) ?? []),
              ].filter((t) => t !== undefined);
              return subRows?.some(
                (subRow) => subRow?.tags && areArraysOfObjectsEqual(subRow?.tags, dp?.datapointTags)
              );
            }
            if (metricRow.tagName && !!metricRow.tags?.length) {
              return metricRow?.tags && areArraysOfObjectsEqual(metricRow?.tags, dp?.datapointTags);
            }
            if (metricRow.tagName) {
              return metricRow.subRows?.some(
                (subRow) => subRow?.tags && areArraysOfObjectsEqual(subRow?.tags, dp?.datapointTags)
              );
            }
            return true;
          });
        totals[timeframe] = resolveMetricCalculation(
          (correctDps ?? [])?.map((x) => ({
            value: Number(x?.value) ?? 0,
            metricRef: x.metricRef,
            tagValues: x.tagValues ?? [],
          })),
          metricRow?.metric?.calculation
        );
        return totals;
      },
      {} as { [key in TimePeriods]: number | string }
    );
  }, [datapointsPerTimeframe, ignoreTags, metricRow]);

  const year = useMemo(() => {
    return isYearly ? totalPerTimeframe?.Year : addUpValues(Object.values(totalPerTimeframe));
  }, [totalPerTimeframe]);

  const latestDate =
    datapoints && new Date(Math.max(...datapoints.map((dp) => new Date(dp.lastEdited).getTime())));
  const latestAuthor =
    datapoints &&
    datapoints.find((dp) => new Date(dp.updatedAt).getTime() === latestDate?.getTime())?.author;
  const latestSource =
    datapoints &&
    datapoints.find((dp) => new Date(dp.updatedAt).getTime() === latestDate?.getTime())?.source;

  return {
    result: {
      ...totalPerTimeframe,
      Year: year,
      latestDate: latestDate,
      latestAuthor: latestAuthor,
      latestSource: latestSource,
    } as {
      [key in TimePeriods]: number;
    } & { latestDate: Date; latestAuthor: ShortUser; latestSource: string },
    isLoading: loading,
  };
};

export const findParentRow = (
  nestedMetrics: MetricsTableData[],
  row: MetricsTableData
): MetricsTableData | undefined => {
  const metricRef = row.metric.reference;

  for (const nestedRow of nestedMetrics) {
    if (nestedRow.metric.reference === metricRef) {
      return nestedRow;
    }

    if (nestedRow.subRows) {
      const result = findParentRow(nestedRow.subRows, row);
      if (result) {
        return result;
      }
    }
  }

  return undefined;
};

export const usePercentageValues = (
  parentRow: MetricsTableData | undefined,
  row: MetricsTableData,
  selectedQuarter: TimePeriods,
  isYearly: boolean,
  aggregatedValue?: number | string | null,
  companyReportingUnitId?: string
) => {
  const parentHasChildrenAndTags =
    !!row.parentMetric && !!row.parentMetric.materialMetrics?.[0]?.materialMetricTags.length;

  const { result: parentTotals } = useAggregatedRowValue(
    parentRow ?? ({} as MetricsTableData),
    isYearly,
    companyReportingUnitId,
    parentHasChildrenAndTags
  );

  const isParent = (row?.subRows?.length ?? 0) > 0;

  if (isParent) return '(100%)';

  return showResultAsPercentage(aggregatedValue, parentTotals[selectedQuarter]);
};

export const generateTagsCombinations = (tagOptions: TagOptions): Combination[] => {
  const combinations: Combination[] = [];

  function recursiveCombinations(index: number, currentCombination: Combination) {
    if (index === tagOptions?.length) {
      combinations.push({ ...currentCombination });
      return;
    }

    const { tagType, valueOptions } = tagOptions[index];

    valueOptions.forEach((option) => {
      if (
        tagOptions.some((op) =>
          op.materialTagValues.some((val: MaterialTagValue) => val.tagValue === option.tagValue)
        )
      ) {
        currentCombination[tagType] = option.tagValue;
        recursiveCombinations(index + 1, currentCombination);
        delete currentCombination[tagType];
      }
    });
  }
  recursiveCombinations(0, {});

  return combinations;
};

const getMultiTagCombinationValues = (combination: Combination) =>
  Object.values(combination).slice(0, -1).join(' - ');

const getMultiTagCombinationType = (combination: Combination) => {
  const arrayCombination = Object.keys(combination);
  const lastItemIndex = arrayCombination?.length - 1;
  return arrayCombination[lastItemIndex] ?? '';
};

const getSingleTagValue = (combination: Combination) =>
  Object.values(combination)[Object.keys(combination)?.length - 1];

export const getTagsCombinationArray = (tags: TagOptions) => {
  const combinations = generateTagsCombinations(tags ?? []);
  const lastTagValueLength = tags[tags.length - 1]?.valueOptions?.length;
  const splitCombinations = [];
  for (let i = 0; i < combinations.length; i += lastTagValueLength) {
    splitCombinations.push(combinations.slice(i, i + lastTagValueLength));
  }
  const allTagsArray = splitCombinations.flatMap((currentCombination) =>
    currentCombination.map((row) =>
      Object.entries(row ?? {}).map(([key, val]) => ({
        tagType: key,
        tagValue: val,
      }))
    )
  );

  return { splitCombinations, allTagsArray };
};

const getNestedTags = (
  metric: AssessableMetrics[number],
  tags: TagOptions,
  materialStandardId: string,
  isGroup: boolean,
  parentMetric?: AssessableMetrics[number],
  materialMetrics?: GroupMaterialMetrics
): MetricsTableData => {
  const hideTags = materialMetrics?.some((mm) =>
    mm?.materialMetricTags?.some((mTag) => !mTag.materialTagValues.length)
  );

  const { splitCombinations } = getTagsCombinationArray(tags);

  const isSingleType = tags?.length === 1;

  if (isSingleType) {
    if (!metric.childrenMetrics?.length)
      return {
        metric: metric,
        type: 'metric',
        tagType: tags?.[0]?.tagType,
        tagCombination: NOTAGCOMBINATION, //this row represents the metric
        subRows: tags?.[0]?.valueOptions.map((value) => ({
          type: 'tag',
          metric: metric,
          tagName: value.tagValue,
          tags: [{ tagType: tags?.[0]?.tagType, tagValue: value.tagValue }],
          tagCombination: getTagsCombination([
            { tagType: tags?.[0]?.tagType ?? '', tagValue: value.tagValue },
          ]),
        })),
        isChild: true,
        parentMetric: parentMetric,
      };

    if (metric.childrenMetrics?.length && hideTags) {
      return {
        metric: metric,
        tagType: tags?.[0]?.tagType,
        type: 'metric',
        tagCombination: NOTAGCOMBINATION,
        subRows: metric.childrenMetrics.map((child) =>
          getNestedRows({
            metric: child?.childMetric as AssessableMetrics[number],
            materialStandardId,
            parentMetric: metric,
            tagFromParent: true,
            isGroup,
          })
        ),
        isChild: false,
        parentMetric: parentMetric,
      };
    }

    return {
      metric: metric,
      tagType: tags?.[0]?.tagType,
      type: 'metric',
      tagCombination: NOTAGCOMBINATION,
      subRows: tags?.[0]?.valueOptions.map((value) => ({
        metric: metric,
        tagName: value.tagValue,
        type: 'tag',
        tagCombination: NOTAGCOMBINATION, //this row doesn't have datapoints, only shows aggregated value
        subRows: metric.childrenMetrics.map((child) =>
          getNestedRows({
            metric: child?.childMetric as AssessableMetrics[number],
            materialStandardId,
            parentMetric: metric,
            tagFromParent: true,
            withSpecialTag: {
              tagName: `${value.tagValue}`,
              tags: [{ tagType: tags?.[0]?.tagType, tagValue: value.tagValue }],
              tagCombination: getTagsCombination([
                { tagType: tags?.[0]?.tagType, tagValue: value.tagValue },
              ]),
            },
            isGroup,
          })
        ),
      })),
      isChild: true,
      parentMetric: parentMetric,
    };
  }

  // Deal with case of multiple tags for children case?
  return {
    metric: metric,
    type: 'metric',
    tagCombination: NOTAGCOMBINATION,
    subRows: splitCombinations?.map((currentCombination) => ({
      metric: metric,
      type: 'tag',
      tagName: getMultiTagCombinationValues(currentCombination[0]),
      tagType: getMultiTagCombinationType(currentCombination[0]),
      tagCombination: NOTAGCOMBINATION,
      subRows: currentCombination
        .filter((comb) => {
          //check if a material metric exists with both tags applied
          if (isGroup) {
            const combinationTagValues = getMultiTagCombinationValues(currentCombination[0]).split(
              ' - '
            );
            if (!materialMetrics?.length) return true;
            if (
              materialMetrics?.some(
                (mm) =>
                  mm.materialMetricTags.some((tag) =>
                    tag.materialTagValues.some((val) => combinationTagValues.includes(val.tagValue))
                  ) &&
                  mm.materialMetricTags.some((tag) =>
                    tag.materialTagValues.some((val) => val.tagValue === getSingleTagValue(comb))
                  )
              )
            ) {
              return true;
            }
            return false;
          }
          return true;
        })
        .map((row) => {
          const tagsObject = Object.entries(row ?? {}).map(([key, val]) => ({
            tagType: key,
            tagValue: val,
          }));
          return {
            metric: metric,
            type: 'tag',
            tagName: getSingleTagValue(row),
            tagCombination: getTagsCombination(tagsObject),
            subRows: !!metric.childrenMetrics?.length
              ? (metric.childrenMetrics?.map((met) => ({
                  metric: met.childMetric!,
                  tags: tagsObject,
                  type: 'metric',
                  isChild: true,
                  tagCombination: getTagsCombination(tagsObject),
                })) as MetricsTableData[])
              : [],
            tags: tagsObject,
          };
        }),
      isChild: true,
    })),
    parentMetric: parentMetric,
  };
};

export const getNestedRows = ({
  metric,
  materialStandardId,
  parentMetric,
  tagFromParent = false,
  withSpecialTag,
  materialMetrics,
  isGroup,
}: {
  metric: AssessableMetrics[number];
  materialStandardId: string;
  parentMetric?: AssessableMetrics[number];
  tagFromParent?: boolean;
  withSpecialTag?: any;
  materialMetrics?: GroupMaterialMetrics;
  isGroup: boolean;
}): MetricsTableData => {
  const tags =
    (metric?.materialMetrics?.length ?? 0) > 0
      ? metric?.materialMetrics.find((mm) => mm.materialStandardId === materialStandardId)
          ?.materialMetricTags
      : metric?.materialMetrics?.flatMap((mm) => mm.materialMetricTags);
  if (!!tags?.some((tag) => tag.materialTagValues.length) && !tagFromParent) {
    return getNestedTags(metric, tags, materialStandardId, isGroup, parentMetric, materialMetrics);
  } else if (metric?.childrenMetrics?.length) {
    return {
      metric: metric,
      type: 'metric',
      subRows: (
        metric?.childrenMetrics?.filter((child) => !!child.childMetric) as Array<{
          childMetric: AssessableMetrics[number];
        }>
      ).map((child) =>
        getNestedRows({
          metric: child.childMetric,
          materialStandardId,
          parentMetric: metric,
          tagFromParent,
          isGroup,
        })
      ),
      isChild: (metric.parentMetrics?.length ?? 0) > 0,
      parentMetric: parentMetric,
      tagCombination: NOTAGCOMBINATION,
    };
  } else {
    return {
      metric: metric,
      type: 'metric',
      isChild: (metric?.parentMetrics?.length ?? 0) > 0,
      parentMetric: parentMetric,
      // tagCombination none if no tags are present
      ...(withSpecialTag ? withSpecialTag : { tagCombination: NOTAGCOMBINATION }),
    };
  }
};

export const getMetricWithChildren = (metric: MetricsTableData) => {
  const childrenMetricRows: MetricsTableData[] = [metric];

  const recurseChildren = (row: MetricsTableData) => {
    if (!row.tagType && !row.tags?.length) {
      row.subRows?.forEach((subRow) => {
        childrenMetricRows.push(subRow);
        recurseChildren(subRow);
      });
    }
  };

  if (metric.subRows?.length) {
    recurseChildren(metric);
  }
  const filteredMetrics =
    (childrenMetricRows.filter((m) => m !== undefined) as MetricsTableData[]) ?? [];
  return filteredMetrics;
};

export const getFlattenedMetricRows = (metric: MetricsTableData) => {
  const flattenedRows: MetricsTableData[] = [metric];

  const recurseRows = (subRows: MetricsTableData[]) => {
    if (!!subRows.length) {
      subRows.forEach((subRow) => {
        flattenedRows.push(subRow);
        recurseRows(subRow.subRows ?? []);
      });
    }
  };

  if (metric?.subRows?.length) {
    recurseRows(metric?.subRows);
  }

  return flattenedRows;
};
